import { FiberManualRecord as DotIcon, TableRows } from '@mui/icons-material';
import { Avatar, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { DateTimeFormat, formatDollars, getAvatarText, getLocalDateTime } from '../../../../common';
import { ServerSideDataTable } from '../../../../common/components/dataTable/serverSide';
import { DatatableColumn, FilterDataType } from '../../../../common/components/dataTable/types';
import { LoadingProgress } from '../../../../common/store/types';
import { PagedRequest, PagedResult } from '../../../../store';
import { theme } from '../../../../themes';
import { ClientCsvRow, ClientItem } from '../store/types';

export interface Props {
  pagedResults?: PagedResult<ClientItem>;
  pagedRequest: PagedRequest;
  hasClientEditAdminOnlyPermission: boolean;
  handlePagedRequest: (pagedRequest: PagedRequest) => void;
  handleCellClick: (clientId: number, status: string, page: string | null) => void;
  handleDownload: (pagedRequest: PagedRequest, mapper: (clients: ClientItem[]) => ClientCsvRow[]) => void;
  downloadProposedOrders: (clientId: number) => void;
  progress: LoadingProgress;
  isDataFromTriumph: boolean;
}

export function ClientListTable(props: Props): JSX.Element {
  const { pagedResults, pagedRequest, handlePagedRequest, handleCellClick, handleDownload, progress, isDataFromTriumph } = props;

  const PortfolioDetail = 'Portfolio Detail';
  function idicatorColor(dataIndex: number): string {
    if ((pagedResults?.results || [])[dataIndex] == null) return '';
    switch (pagedResults?.results[dataIndex].bandName) {
      case 'S0':
        return theme.palette.common.white;
      case 'S1':
        return theme.palette.green?.main;
      case 'S2':
        return theme.palette.secondary.main;
      case 'S3':
        return theme.palette.primary.main;
      case 'S4':
        return theme.palette.orange?.main;
      case 'S5':
        return theme.palette.error.main;
      case 'P1':
        return theme.palette.green?.main;
      case 'P2':
        return theme.palette.orange?.main;
      case 'P3':
        return theme.palette.error.main;
      default:
        return theme.palette.common.white;
    }
  }

  const outOfBalanceIndicatorColumn = (dataIndex: number): React.ReactNode => <DotIcon htmlColor={idicatorColor(dataIndex)} />;

  const avatarColumn = (dataIndex: number): React.ReactNode => (
    <Avatar style={{ backgroundColor: theme.palette.primary.main }}>
      <Typography variant="h5">
        {(pagedResults?.results || [])[dataIndex] == null ? '' : getAvatarText(pagedResults?.results[dataIndex].accountName)}
      </Typography>
    </Avatar>
  );

  const accountNameColumn = (dataIndex: number): React.ReactNode => (
    <Typography variant="h5" data-testid={`accountName_${dataIndex}`}>
      {(pagedResults?.results || [])[dataIndex] == null ? '' : pagedResults?.results[dataIndex].accountName}
    </Typography>
  );

  const typeColumn = (dataIndex: number): React.ReactNode => {
    if ((pagedResults?.results || [])[dataIndex] == null) return '';

    const accountType = pagedResults?.results[dataIndex].accountType;
    const accountSubType = pagedResults?.results[dataIndex].accountSubType;

    let dspType: string | undefined = '';

    switch (accountType) {
      case 'Individual - Super': {
        dspType = 'Super';
        break;
      }
      case 'Individual - Pension': {
        dspType = 'Pension';
        break;
      }
      case 'Individual - Non Super': {
        dspType = 'Individual';
        break;
      }
      default: {
        dspType = accountType;
        break;
      }
    }

    if (accountSubType) {
      dspType = `${dspType} - ${accountSubType}`;
    }
    return (
      <Typography variant="h5" data-testid={`type_${dataIndex}`}>
        {!!dspType ? dspType : ''}
      </Typography>
    );
  };

  const executionDateColumn = (dataIndex: number): React.ReactNode => {
    if ((pagedResults?.results || [])[dataIndex] == null) return '';
    return (
      <Typography variant="h5" data-testid={`executionDate_${dataIndex}`}>
        {getLocalDateTime(pagedResults?.results[dataIndex].executionDate, DateTimeFormat.Short)}
      </Typography>
    );
  };

  const feeAniversaryDateColumn = (dataIndex: number): React.ReactNode => {
    if ((pagedResults?.results || [])[dataIndex] == null) return '';
    return (
      <Typography variant="h5" data-testid={`feeAniversaryDate_${dataIndex}`}>
        {getLocalDateTime(pagedResults?.results[dataIndex].feeAnniversaryDate, DateTimeFormat.Short)}
      </Typography>
    );
  };

  const adviserNameColumn = (dataIndex: number): React.ReactNode => (
    <Typography variant="h5" data-testid={`adviserName_${dataIndex}`}>
      {(pagedResults?.results || [])[dataIndex] == null ? '' : pagedResults?.results[dataIndex].adviserName}
    </Typography>
  );

  const superMemberNumberColumn = (dataIndex: number): React.ReactNode => (
    <>
      {isDataFromTriumph && (
        <Typography variant="h5" data-testid={`status_${dataIndex}`}>
          {(pagedResults?.results || [])[dataIndex] == null ? '' : pagedResults?.results[dataIndex].superMemberNumber}
        </Typography>
      )}
    </>
  );

  const statusColumn = (dataIndex: number): React.ReactNode => (
    <Typography variant="h5" data-testid={`status_${dataIndex}`}>
      {(pagedResults?.results || [])[dataIndex] == null ? '' : pagedResults?.results[dataIndex].status}
    </Typography>
  );

  const instructIdColumn = (dataIndex: number): React.ReactNode => {
    if ((pagedResults?.results || [])[dataIndex] == null) return '';

    const instructId = pagedResults?.results[dataIndex].instructId;

    return (
      <Typography variant="h5" data-testid={`instructId_${dataIndex}`}>
        {!!instructId ? instructId : ''}
      </Typography>
    );
  };

  const cashBalanceColumn = (dataIndex: number): React.ReactNode => {
    if ((pagedResults?.results || [])[dataIndex] == null) return '';

    const cashBalance = pagedResults?.results[dataIndex].cashBalance;

    return (
      <Typography variant="h5" data-testid={`cashBalance_${dataIndex}`}>
        {!!cashBalance ? formatDollars(cashBalance) : ''}
      </Typography>
    );
  };

  const marketValueColumn = (dataIndex: number): React.ReactNode => {
    if ((pagedResults?.results || [])[dataIndex] == null) return '';

    const marketValue = pagedResults?.results[dataIndex].marketValueWithPendingIncome;

    return (
      <Typography variant="h5" data-testid={`marketValue_${dataIndex}`}>
        {!!marketValue ? formatDollars(marketValue) : ''}
      </Typography>
    );
  };

  const GetCellProps = (rowIndex: number) => {
    return {
      onClick: () => {
        const result = getRowDetails(rowIndex);
        handleCellClick(result.clientId ?? 0, result.status ?? 'Active', '');
      },
    };
  };

  const getRowDetails = (rowIndex: number) => {
    return {
      clientId: pagedResults?.results[rowIndex].clientId,
      status: pagedResults?.results[rowIndex].status,
    };
  };

  const actionsColumn = (dataIndex: number): React.ReactNode => {
    return (
      <Tooltip title="Portfolio Detail" arrow>
        <IconButton
          disableFocusRipple
          data-testid={`view_portfolio_detail_${dataIndex}`}
          disableRipple
          onClick={() => {
            const result = getRowDetails(dataIndex);
            if (!!result.clientId && !!result.status) {
              handleCellClick(result.clientId, result.status, PortfolioDetail);
            }
          }}
        >
          <TableRows color="primary" />
        </IconButton>
      </Tooltip>
    );
  };

  const columns: DatatableColumn[] = [
    {
      name: 'actions',
      label: ' ',
      textAlign: 'right',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number): React.ReactNode => actionsColumn(dataIndex),
      },
    },
    {
      name: 'outOfBalanceIndicator',
      label: ' ',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number) => outOfBalanceIndicatorColumn(dataIndex),
        setCellProps(_cellValue, rowIndex) {
          return GetCellProps(rowIndex);
        },
      },
    },
    {
      filterDataType: FilterDataType.string,
      name: 'avatar',
      label: ' ',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => avatarColumn(dataIndex),
        setCellProps(_cellValue, rowIndex) {
          return GetCellProps(rowIndex);
        },
      },
    },
    {
      filterDataType: FilterDataType.string,
      name: 'accountName',
      label: 'CLIENT NAME',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex: number) => accountNameColumn(dataIndex),
        setCellProps(_cellValue, rowIndex) {
          return GetCellProps(rowIndex);
        },
      },
    },
    {
      filterDataType: FilterDataType.string,
      name: 'superMemberNumber',
      label: 'SUPER MEMBER NO.',
      options: {
        filter: true,
        sort: true,
        display: isDataFromTriumph,
        customBodyRenderLite: (dataIndex: number) => superMemberNumberColumn(dataIndex),
        setCellProps(_cellValue, rowIndex) {
          return GetCellProps(rowIndex);
        },
      },
    },
    {
      filterDataType: FilterDataType.string,
      name: 'instructId',
      label: 'INSTRUCT ID',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex: number) => instructIdColumn(dataIndex),
        setCellProps(_cellValue, rowIndex) {
          return GetCellProps(rowIndex);
        },
      },
    },
    {
      filterDataType: FilterDataType.string,
      name: 'accountType',
      label: 'TYPE',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex: number) => typeColumn(dataIndex),
        setCellProps(_cellValue, rowIndex) {
          return GetCellProps(rowIndex);
        },
      },
    },
    {
      filterDataType: FilterDataType.date,
      textAlign: 'right',
      name: 'executionDate',
      label: 'SERVICES AGREEMENT DATE',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex: number) => executionDateColumn(dataIndex),
        setCellProps(_cellValue, rowIndex) {
          return GetCellProps(rowIndex);
        },
      },
    },
    {
      filterDataType: FilterDataType.date,
      textAlign: 'right',
      name: 'feeAnniversaryDate',
      label: 'REFERENCE DATE',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex: number) => feeAniversaryDateColumn(dataIndex),
        setCellProps(_cellValue, rowIndex) {
          return GetCellProps(rowIndex);
        },
      },
    },
    {
      filterDataType: FilterDataType.string,
      name: 'adviserName',
      label: 'ADVISER',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex: number) => adviserNameColumn(dataIndex),
        setCellProps(_cellValue, rowIndex) {
          return GetCellProps(rowIndex);
        },
      },
    },
    {
      filterDataType: FilterDataType.string,
      name: 'status',
      label: 'STATUS',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex: number) => statusColumn(dataIndex),
        setCellProps(_cellValue, rowIndex) {
          return GetCellProps(rowIndex);
        },
      },
    },
    {
      filterDataType: FilterDataType.numeric,
      textAlign: 'right',
      name: 'cashBalance',
      label: 'CASH BALANCE',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex: number) => cashBalanceColumn(dataIndex),
        setCellProps(_cellValue, rowIndex) {
          return GetCellProps(rowIndex);
        },
      },
    },
    {
      filterDataType: FilterDataType.numeric,
      textAlign: 'right',
      name: 'marketValue',
      label: 'MARKET VALUE',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex: number) => marketValueColumn(dataIndex),
        setCellProps(_cellValue, rowIndex) {
          return GetCellProps(rowIndex);
        },
      },
    },
  ];

  return (
    <Paper elevation={0}>
      <ServerSideDataTable
        loadingProgress={progress}
        columns={columns}
        pagedRequest={pagedRequest}
        pagedResult={pagedResults}
        csvDownload={() =>
          handleDownload(pagedRequest, (clients) =>
            clients.map((c) => {
              return {
                band: c.bandName,
                client: c.accountName,
                type: !!c.accountSubType ? `${c.accountType} - ${c.accountSubType}` : c.accountType,
                inceptionDate: getLocalDateTime(c.inceptionDate, DateTimeFormat.Short),
                advisor: c.adviserName,
                status: c.status,
                cashBalance: c.cashBalance,
                marketValue: c.marketValue,
                superMemberNumber: c.superMemberNumber,
                instructId: c.instructId,
              };
            })
          )
        }
        options={{
          filter: true,
          viewColumns: true,
        }}
        handlePagedRequest={handlePagedRequest}
      ></ServerSideDataTable>
    </Paper>
  );
}
